import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BookOpen, Users, ShoppingCart, Settings, Package, Tag, User, Menu, X, LogOut, ChevronDown, LayoutDashboard, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useWorkspace } from '../contexts/WorkspaceContext';
interface AdminLayoutProps {
  children: React.ReactNode;
}
export function AdminLayout({
  children
}: AdminLayoutProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    setWorkspace
  } = useWorkspace();
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (switcherRef.current && !switcherRef.current.contains(e.target as Node)) {
        setSwitcherOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleSwitchToStudent = () => {
    setWorkspace('student');
    setSwitcherOpen(false);
    navigate('/student/dashboard');
  };
  const navigation = [{
    group: 'MANAGE PROFILE',
    items: [{
      name: 'Manage Profile',
      href: '/profile',
      icon: User
    }]
  }, {
    group: 'COURSE ADMIN',
    items: [{
      name: 'Manage Courses',
      href: '/courses',
      icon: BookOpen
    }, {
      name: 'Course Sections',
      href: '/sections',
      icon: BookOpen
    }, {
      name: 'Export Course',
      href: '/export-course',
      icon: BookOpen
    }, {
      name: 'Export Users',
      href: '/export-users',
      icon: Users
    }, {
      name: 'Manage Questions',
      href: '/questions',
      icon: BookOpen
    }]
  }, {
    group: 'MANAGEMENT',
    items: [{
      name: 'Users',
      href: '/users',
      icon: Users
    }, {
      name: 'Orders',
      href: '/orders',
      icon: ShoppingCart
    }, {
      name: 'Admin Area',
      href: '/admin-area',
      icon: Settings
    }]
  }, {
    group: 'PACKAGES',
    items: [{
      name: 'Manage Packages',
      href: '/packages',
      icon: Package
    }, {
      name: 'Manage Coupons',
      href: '/coupons',
      icon: Tag
    }]
  }];
  return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-200" data-mp="3qp93rzwipbml">
      {/* Top Bar */}
      <header className="bg-white border-b border-slate-200 h-16 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30" data-id="element-201" data-mp="3qrbpi7qn0ili">
        <div className="flex items-center gap-4" data-id="element-202" data-mp="218dghwsnghr6">
          <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="p-2 hover:bg-slate-100 rounded-md lg:hidden" data-id="element-203" data-mp="217veejjb39bc">
            <Menu className="h-6 w-6 text-slate-600" data-id="element-204" data-mp="2b61eqwpgs7e3" />
          </button>
          <h1 className="text-lg font-semibold text-slate-800 hidden sm:block" data-id="element-205" data-mp="2f7wcxsc3m4nn">
            Welcome to Administration Area
          </h1>
          {/* Workspace Switcher in top bar */}
          <div ref={switcherRef} className="relative hidden sm:block" data-id="element-206" data-mp="1ifssotrt52u8">
            <button onClick={() => setSwitcherOpen(!switcherOpen)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors text-sm" data-id="element-207" data-mp="1nh8dckq2p7c3">
              <span className="inline-block w-2 h-2 rounded-full bg-green-400" data-id="element-208" data-mp="zrnvy69kii4x"></span>
              <span className="font-medium text-slate-700" data-id="element-209" data-mp="13spu0jrr0o5b">Site Admin</span>
              <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${switcherOpen ? 'rotate-180' : ''}`} data-id="element-210" data-mp="13qmv1a8mbkog" />
            </button>

            {switcherOpen && <div className="absolute top-full left-0 mt-2 w-52 bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden z-50" data-id="element-211" data-mp="fn6u1j6u46t8">
                <div className="px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider border-b border-slate-100" data-id="element-212" data-mp="pm65f9izigjx">
                  Switch Workspace
                </div>
                <button onClick={() => setSwitcherOpen(false)} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-slate-50 transition-colors bg-slate-50/80" data-id="element-213" data-mp="ygaq1b7iyvr0">
                  <div className="w-7 h-7 rounded-md bg-slate-800 flex items-center justify-center flex-shrink-0" data-id="element-214" data-mp="18e8bxs1wwggg">
                    <ShieldCheck className="h-4 w-4 text-white" data-id="element-215" data-mp="3f695iw4myt3k" />
                  </div>
                  <div className="text-left" data-id="element-216" data-mp="3a6wjuepi3s2k">
                    <p className="font-medium text-slate-800" data-id="element-217" data-mp="1pn9h2iu97ebk">Site Admin</p>
                    <p className="text-xs text-slate-500" data-id="element-218" data-mp="1ppdohard2vgl">Currently active</p>
                  </div>
                  <span className="ml-auto w-1.5 h-1.5 rounded-full bg-green-400" data-id="element-219" data-mp="1unxcrhtfgjc4"></span>
                </button>
                <button onClick={handleSwitchToStudent} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-purple-50 transition-colors" data-id="element-220" data-mp="1kw19ztgec51k">
                  <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{
                backgroundColor: '#4A2D8F'
              }} data-id="element-221" data-mp="1aymvw0hy3scf">
                    <LayoutDashboard className="h-4 w-4 text-white" data-id="element-222" data-mp="11xyohon24q8i" />
                  </div>
                  <div className="text-left" data-id="element-223" data-mp="2rcwk8um6th39">
                    <p className="font-medium text-slate-800" data-id="element-224" data-mp="2wb7tb34nrnkf">Student Portal</p>
                    <p className="text-xs text-slate-500" data-id="element-225" data-mp="2w93s4sy592nu">
                      Student environment
                    </p>
                  </div>
                </button>
              </div>}
          </div>
        </div>

        <div className="flex items-center gap-4" data-id="element-226" data-mp="2kvla1pgyffxr">
          <span className="text-sm text-slate-600 hidden sm:inline-block" data-id="element-227" data-mp="2kwd2wsj62zkc">
            Logged in as{' '}
            <span className="font-medium text-slate-900" data-id="element-228" data-mp="2fz4nv0tbfbnb">Admin User</span>
          </span>
          <Button variant="outline" size="sm" className="gap-2" onClick={() => window.location.href = 'https://www.desertsageseminars.com'} data-id="element-229" data-mp="3dk0jzxl8b828">
            <LogOut className="h-4 w-4" data-id="element-230" data-mp="2yvw0wsi9kuix" />
            Log-off
          </Button>
        </div>
      </header>

      <div className="flex flex-1 relative" data-id="element-231" data-mp="5m6ds6xzt0y">
        {/* Sidebar */}
        <aside className={`
            fixed inset-y-0 left-0 z-20 w-64 bg-slate-900 text-slate-300 transform transition-transform duration-200 ease-in-out lg:translate-x-0 lg:static lg:inset-auto
            ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          `} data-id="element-232" data-mp="7yw4rixz7ok">
          <div className="h-full overflow-y-auto p-4" data-id="element-233" data-mp="7rhnbl2ca788">
            <div className="flex items-center justify-between mb-4 lg:hidden" data-id="element-234" data-mp="cngcvbzelva8">
              <span className="text-xl font-bold text-white" data-id="element-235" data-mp="ho4684b3uo7m">Menu</span>
              <button onClick={() => setIsSidebarOpen(false)} className="p-2 text-slate-400 hover:text-white" data-id="element-236" data-mp="mmnrs3lsdxzg">
                <X className="h-6 w-6" data-id="element-237" data-mp="qmpveib311hd" />
              </button>
            </div>

            {/* Logo - full width, no switcher */}
            <div className="mb-6 px-2" data-id="element-238" data-mp="vhbwnsng9dnq">
              <img src="https://cdn.magicpatterns.com/uploads/ghnAoYdYcsrnAteaBPC6VB/DSS-logo-notext.png" alt="Desert Sage Seminars" className="w-full max-w-[180px] h-auto" data-id="element-239" data-mp="10ec4o93v0qhh" />
            </div>

            <nav className="space-y-8" data-id="element-240" data-mp="1a9vx2lj7t71h">
              {navigation.map(group => <div key={group.group} data-id="element-241" data-mp="1vldq1c97bhko">
                  <h3 className="px-2 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3" data-id="element-242" data-mp="1qon80jdu6bwm">
                    {group.group}
                  </h3>
                  <div className="space-y-1" data-id="element-243" data-mp="24lskp282290u">
                    {group.items.map(item => {
                  const isActive = location.pathname === item.href;
                  return <Link key={item.name} to={item.href} className={`
                            group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors
                            ${isActive ? 'bg-slate-800 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'}
                          `} data-id="element-244" data-mp="1bx4ssnsswleb">
                          <item.icon className={`mr-3 h-5 w-5 flex-shrink-0 ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-white'}`} data-id="element-245" data-mp="oaevu0vb7h0k" />
                          {item.name}
                        </Link>;
                })}
                  </div>
                </div>)}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-8 w-full" data-id="element-246" data-mp="2vtaozd9hort3">
          <div className="max-w-7xl mx-auto" data-id="element-247" data-mp="3jmanf038b04b">{children}</div>
        </main>

        {/* Mobile Overlay */}
        {isSidebarOpen && <div className="fixed inset-0 bg-black/50 z-10 lg:hidden" onClick={() => setIsSidebarOpen(false)} data-id="element-248" data-mp="3nk8fcaodw0be" />}
      </div>
    </div>;
}